/**
 * BMM — Product Grid
 * File: bmm-product-grid.js
 * Version: 2.0
 * Fixes:
 *   - View More / View Less toggle (Option A)
 *   - aria-hidden management on tab panels
 *   - Tab switch resets View More state correctly
 *   - Card JS re-init guard per panel
 */
(function () {
  'use strict';

  function initGrid(section) {
    var tabs   = Array.from(section.querySelectorAll('.bmm-pg__tab'));
    var panels = Array.from(section.querySelectorAll('.bmm-pg__panel'));

    if (!tabs.length) return;

    /* ── TAB SWITCHING ── */
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = parseInt(tab.dataset.tab, 10);

        /* Update tabs */
        tabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        /* Update panels + aria-hidden */
        panels.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-hidden', 'true');
        });

        var activePanel = panels.find(function (p) {
          return parseInt(p.dataset.tab, 10) === idx;
        });

        if (activePanel) {
          activePanel.classList.add('is-active');
          activePanel.setAttribute('aria-hidden', 'false');
          initCards(activePanel);
          initViewMore(activePanel);
        } else if (panels[0]) {
          panels[0].classList.add('is-active');
          panels[0].setAttribute('aria-hidden', 'false');
          initCards(panels[0]);
          initViewMore(panels[0]);
        }
      });
    });

    /* Init first panel on load */
    if (panels[0]) {
      panels[0].setAttribute('aria-hidden', 'false');
      initCards(panels[0]);
      initViewMore(panels[0]);
    }
  }

  /* ══ VIEW MORE / VIEW LESS TOGGLE ══ */
  function initViewMore(panel) {
    var btn = panel.querySelector('.bmm-pg__viewmore');
    if (!btn) return;

    /* Reset state on every tab switch */
    btn.dataset.bmmVmInit = '0';
    btn.classList.remove('is-done', 'is-less');

    /* Re-collect hidden cards (may have changed if panel was reset) */
    var allCards    = Array.from(panel.querySelectorAll('.bmm-pc'));
    var hiddenCards = allCards.filter(function (c) {
      return parseInt(c.dataset.cardIndex, 10) > 8;
    });
    var moreLabel   = btn.dataset.moreLabel || 'View More';
    var lessLabel   = btn.dataset.lessLabel || 'View Less';
    var hiddenCount = btn.dataset.hiddenCount || hiddenCards.length;
    var expanded    = false;

    /* Ensure initial state: cards > 8 are hidden */
    hiddenCards.forEach(function (card) {
      card.classList.add('bmm-pg__hidden');
    });

    /* Update button label */
    function setMoreLabel() {
      btn.innerHTML = moreLabel +
        ' <span class="bmm-pg__viewmore-count">(' + hiddenCount + ' more)</span>';
      btn.classList.remove('is-less');
      btn.setAttribute('aria-expanded', 'false');
    }

    function setLessLabel() {
      btn.innerHTML = lessLabel;
      btn.classList.add('is-less');
      btn.setAttribute('aria-expanded', 'true');
    }

    if (hiddenCards.length === 0) {
      btn.classList.add('is-done');
      return;
    }

    setMoreLabel();

    /* Remove old listener by cloning */
    var newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);
    btn = newBtn;

    btn.addEventListener('click', function () {
      if (!expanded) {
        /* EXPAND — show all hidden cards */
        hiddenCards.forEach(function (card) {
card.classList.add('is-visible');
        });
        initCards(panel);
        setLessLabel();
        expanded = true;

        /* Scroll to first revealed card */
        if (hiddenCards[0]) {
          hiddenCards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } else {
        /* COLLAPSE — hide cards back */
        hiddenCards.forEach(function (card) {
          card.classList.add('bmm-pg__hidden');
        });
        setMoreLabel();
        expanded = false;

        /* Scroll back to top of grid section */
        var grid = panel.querySelector('.bmm-pg__grid');
        if (grid) {
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }

  /* ══ CARD INTERACTIONS ══ */
  function initCards(container) {
    var cards = container.querySelectorAll('.bmm-pc');

    cards.forEach(function (card) {
      if (card.dataset.bmmInit === '1') return;
      card.dataset.bmmInit = '1';

      var variantMap = {};
      var cartBtn    = card.querySelector('.bmm-pc__cart');
      var sizeBtns   = card.querySelectorAll('.bmm-pc__sz');
      var feedback   = card.querySelector('.bmm-pc__cart-feedback');

      try {
        variantMap = JSON.parse(card.dataset.variants || '{}');
      } catch (e) { variantMap = {}; }

      /* Size selection */
      sizeBtns.forEach(function (btn) {
        if (btn.disabled) return;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          sizeBtns.forEach(function (b) { b.classList.remove('sel'); });
          btn.classList.add('sel');
          var v = variantMap[btn.dataset.size];
          if (cartBtn && v) cartBtn.dataset.variantId = v.id;
        });
      });

      /* Pre-populate for single-variant products */
      if (card.dataset.defaultVariant && cartBtn) {
        cartBtn.dataset.variantId = card.dataset.defaultVariant;
      }

      /* Cart add */
      if (cartBtn) {
        cartBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var variantId = cartBtn.dataset.variantId;

          if (!variantId || variantId === '') {
            card.classList.add('bmm-shake');
            setTimeout(function () { card.classList.remove('bmm-shake'); }, 500);
            if (feedback) {
              feedback.textContent = 'Select a size';
              setTimeout(function () { feedback.textContent = ''; }, 1800);
            }
            return;
          }

          if (cartBtn.classList.contains('bmm-loading')) return;
          cartBtn.classList.add('bmm-loading');
          if (feedback) feedback.textContent = 'Adding\u2026';

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          })
          .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
          .then(function () {
            cartBtn.classList.remove('bmm-loading');
            cartBtn.classList.add('bmm-added');
            if (feedback) feedback.textContent = 'Added!';
            updateCartCount();
            openCartDrawer();
            setTimeout(function () {
              cartBtn.classList.remove('bmm-added');
              if (feedback) feedback.textContent = '';
            }, 2200);
          })
          .catch(function () {
            cartBtn.classList.remove('bmm-loading');
            if (feedback) feedback.textContent = 'Error';
            setTimeout(function () { if (feedback) feedback.textContent = ''; }, 1800);
          });
        });
      }
    });
  }

  function updateCartCount() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var n = c.item_count;
      ['.cart-count', '.cart-count-bubble', '[data-cart-count]'].forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          el.textContent = n;
          if (n > 0) el.removeAttribute('hidden');
        });
      });
    }).catch(function () {});
  }

  function openCartDrawer() {
    ['cart:open', 'cart:refresh', 'theme:cart:open'].forEach(function (ev) {
      document.dispatchEvent(new CustomEvent(ev));
    });
  }

  /* ══ INIT ══ */
  function initAll() {
    document.querySelectorAll('.bmm-pg[data-section-id]').forEach(initGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('.bmm-pg[data-section-id]');
    if (s) initGrid(s);
  });

})();
